import { Protect } from "@clerk/clerk-react"
import {
  Badge,
  Group,
  NavLink,
  ThemeIcon,
  Tooltip,
  UnstyledButton,
} from "@mantine/core"
import { useHover } from "@mantine/hooks"
import { useFeatureFlag, User } from "configcat-react"
import _ from "lodash"
import { FC } from "react"
import { NavLink as BaseNavLink } from "react-router-dom"

import { OnboardingResponses } from "@costory/types/endpoints/onboard"
import { OnboardingStatus } from "@costory/types/prisma-client"

import { SidebarItem } from "@costory/front/components/layout/constants"
import { Permissions } from "@costory/front/utils/permissions"

type Props = {
  route: SidebarItem
  isCollapsed: boolean
  user?: User
  isParent?: boolean
  onboardingStatus?: OnboardingResponses.Status
}

export const SidebarLink: FC<Props> = ({
  route,
  user,
  isCollapsed,
  isParent = false,
  onboardingStatus,
}) => {
  const currentPath = window.location.pathname
  const shouldBeOpened =
    route.links &&
    route.links
      .map((el) => el.path)
      .filter((el) => !!el)
      .includes(currentPath.replace("/", ""))
  const isFF = "isFeatureFlagged" in route
  const { ref, hovered } = useHover()
  const isActive =
    currentPath === `/${route.path}` || currentPath.includes(`/${route.path}`)

  const remainingSteps = onboardingStatus
    ? (() => {
        const setupRemaining = _(onboardingStatus.setupSteps)
          .filter((step) => !step.completed && !step.skipped)
          .size()
        const onboardingRemaining = _(onboardingStatus.onboardingSteps)
          .filter((step) => !step.completed && !step.skipped)
          .size()
        return setupRemaining + onboardingRemaining
      })()
    : 0

  const showBadge = route.path === "onboarding" && remainingSteps > 0
  const { value: isEnabled, loading } = useFeatureFlag(
    isFF ? (route.path || "").replace(/[-/]/g, "") : "alwaysvalid",
    false,
    user,
  )

  if (isFF && (loading || !isEnabled)) {
    return (
      // empty component otherwise the hover breaks
      <UnstyledButton hidden />
    )
  }
  return (
    <Protect permission={route.role || Permissions.Member}>
      <Tooltip
        label={
          showBadge
            ? `${remainingSteps} step${remainingSteps > 1 ? "s" : ""} remaining`
            : route.tooltip
        }
        disabled={!route.tooltip}
        position="right"
        offset={24}
      >
        <NavLink
          ref={ref}
          leftSection={
            <ThemeIcon c="inherit" size="sm">
              {route.icon && <route.icon />}
            </ThemeIcon>
          }
          c={isParent ? "textSidebar" : "textSidebarMuted"}
          to={route.path || "#"}
          component={BaseNavLink}
          key={route.name}
          defaultOpened={shouldBeOpened}
          opened={isCollapsed ? false : undefined}
          label={
            showBadge ? (
              <Group justify="space-between" wrap="nowrap">
                <span>{route.name}</span>
                <Badge size="xs" variant="light" color="blue">
                  {remainingSteps}
                </Badge>
              </Group>
            ) : (
              route.name
            )
          }
          bg={
            hovered
              ? "surfaceSidebarHover"
              : isActive
                ? undefined // Let Mantine handle active state styling
                : "surfaceSidebar"
          }
        >
          {route.links?.map((link, index) => {
            const isNestedGreyedOut =
              !link.onboarding &&
              onboardingStatus?.onboardingStatus === OnboardingStatus.SETUP

            return (
              <div
                key={route.path}
                style={{
                  opacity: isNestedGreyedOut ? 0.5 : 1,
                  pointerEvents: isNestedGreyedOut ? "none" : "auto",
                }}
              >
                <SidebarLink
                  route={link}
                  isCollapsed={isCollapsed}
                  key={index}
                  user={user}
                  onboardingStatus={onboardingStatus}
                />
              </div>
            )
          })}
        </NavLink>
      </Tooltip>
    </Protect>
  )
}
